import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { ComboboxInputField, DateInputField, FormattedNumberInputField, InputField, SelectInputField } from "@/components/shared/form";
import { provincesSimplified } from "@/constants/provinces";
import { SelectItem } from "@/components/ui/select";
import { BasisForBonusType } from "@/graphql/__generated__/graphql";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCreateTariffMutationHook } from "@/hooks";
import { CircleLoader } from "@/components/shared/loaders";
import { convertDecimalToBp } from "@/utils/helpers";

const createTariffSchema = (t: TFunction) => z.object({
  province: z.string({ message: t("schemas:form.required") }),
  workdaysPerYear: z.number({ message: t("schemas:form.required") }),
  startNight: z.string({
    message: t("schemas:form.required"),
  }).refine(
    (val) => /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(val),
    { message: t("schemas:form.required") }
  ),
  endNight: z.string({
    message: t("schemas:form.required"),
  }).refine(
    (val) => /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(val),
    { message: t("schemas:form.required") }
  ),
  basisForBonusCalculation: z.enum(BasisForBonusType, {
    message: t("schemas:form.required"),
  }),
  bonusNightAbsolute: z.number({ message: t("schemas:form.required") }),
  bonusNightRelative: z.number({ message: t("schemas:form.required") }),
  bonusSundayAbsolute: z.number({ message: t("schemas:form.required") }),
  bonusSundayRelative: z.number({ message: t("schemas:form.required") }),
  bonusSundayNightAbsolute: z.number({ message: t("schemas:form.required") }),
  bonusSundayNightRelative: z.number({ message: t("schemas:form.required") }),
  bonusHolidayAbsolute: z.number({ message: t("schemas:form.required") }),
  bonusHolidayRelative: z.number({ message: t("schemas:form.required") }),
  bonusHolidayNightAbsolute: z.number({ message: t("schemas:form.required") }),
  bonusHolidayNightRelative: z.number({ message: t("schemas:form.required") }),
  activeFrom: z.date({
    error: t("schemas:form.required"),
  }),
});

const createTariffFormInputs = (t: TFunction) => {
  return [
    {
      groupName: "Night Bonus",
      fields: [
        {
          name: "Night",
          inputs: [
            {
              name: "bonusNightRelative",
              label: t("tariff.inputs.bonusNightRelative.label"),
              placeholder: t("tariff.inputs.bonusNightRelative.placeholder"),
              style: "decimal",
              additionalSuffix: "%"
            },
            {
              name: "bonusNightAbsolute",
              label: t("tariff.inputs.bonusNightAbsolute.label"),
              placeholder: t("tariff.inputs.bonusNightAbsolute.placeholder"),
              style: "currency"
            }
          ]
        }
      ]
    },
    {
      groupName: "Sunday Bonus",
      fields: [
        {
          name: "Day",
          inputs: [
            {
              name: "bonusSundayRelative",
              label: t("tariff.inputs.bonusSundayRelative.label"),
              placeholder: t("tariff.inputs.bonusSundayRelative.placeholder"),
              style: "decimal",
              additionalSuffix: "%"
            },
            {
              name: "bonusSundayAbsolute",
              label: t("tariff.inputs.bonusSundayAbsolute.label"),
              placeholder: t("tariff.inputs.bonusSundayAbsolute.placeholder"),
              style: "currency"
            },
          ]
        },
        {
          name: "Night",
          inputs: [
            {
              name: "bonusSundayNightRelative",
              label: t("tariff.inputs.bonusSundayNightRelative.label"),
              placeholder: t("tariff.inputs.bonusSundayNightRelative.placeholder"),
              style: "decimal",
              additionalSuffix: "%"
            },
            {
              name: "bonusSundayNightAbsolute",
              label: t("tariff.inputs.bonusSundayNightAbsolute.label"),
              placeholder: t("tariff.inputs.bonusSundayNightAbsolute.placeholder"),
              style: "currency"
            },
          ]
        }
      ],
    },
    {
      groupName: "Holiday Bonus",
      fields: [
        {
          name: "Day",
          inputs: [
            {
              name: "bonusHolidayRelative",
              label: t("tariff.inputs.bonusHolidayRelative.label"),
              placeholder: t("tariff.inputs.bonusHolidayRelative.placeholder"),
              style: "decimal",
              additionalSuffix: "%"
            },
            {
              name: "bonusHolidayAbsolute",
              label: t("tariff.inputs.bonusHolidayAbsolute.label"),
              placeholder: t("tariff.inputs.bonusHolidayAbsolute.placeholder"),
              style: "currency"
            },
          ]
        },
        {
          name: "Night",
          inputs: [
            {
              name: "bonusHolidayNightRelative",
              label: t("tariff.inputs.bonusHolidayNightRelative.label"),
              placeholder: t("tariff.inputs.bonusHolidayNightRelative.placeholder"),
              style: "decimal",
              additionalSuffix: "%"
            },
            {
              name: "bonusHolidayNightAbsolute",
              label: t("tariff.inputs.bonusHolidayNightAbsolute.label"),
              placeholder: t("tariff.inputs.bonusHolidayNightAbsolute.placeholder"),
              style: "currency"
            },
          ]
        }
      ],
    }
  ];
};

interface AddTariffDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

function AddTariffDialog({ open, onOpenChange }: AddTariffDialogProps) {
  const { t } = useTranslation(["modals", "common"]);

  const { createTariffMutation, loading: createTariffLoading } = useCreateTariffMutationHook();

  const tariffSchema = createTariffSchema(t);
  const form = useForm<z.infer<typeof tariffSchema>>({
    resolver: zodResolver(tariffSchema),
    mode: "onChange"
  });

  const onSubmit = (data: z.infer<typeof tariffSchema>) => {
    const input = {
      ...data,
      bonusNightRelative: convertDecimalToBp(data.bonusNightRelative),
      bonusSundayRelative: convertDecimalToBp(data.bonusSundayRelative),
      bonusSundayNightRelative: convertDecimalToBp(data.bonusSundayNightRelative),
      bonusHolidayRelative: convertDecimalToBp(data.bonusHolidayRelative),
      bonusHolidayNightRelative: convertDecimalToBp(data.bonusHolidayNightRelative),
    };

    createTariffMutation({ variables: { input } }).then(() => {
      onOpenChange(false);
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof tariffSchema>>) => {
    console.error(errors);
  };

  const formInputs = createTariffFormInputs(t);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="px-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="edit-tariff-form">
            <DialogHeader className="px-4">
              <DialogTitle>
                {t("tariff.title")}
              </DialogTitle>
              <DialogDescription>
                {t("tariff.description")}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea>
              <div className="flex flex-col gap-4 max-h-[75vh] py-2 pl-4 pr-4">
                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Province */}
                  <ComboboxInputField
                    form={form}
                    name="province"
                    label={t("tariff.inputs.province.label")}
                    options={provincesSimplified.map((province) => ({
                      label: province.name,
                      value: province.code
                    }))}
                    className="w-full"
                  />

                  {/* Activation Date */}
                  <DateInputField
                    form={form}
                    name="activeFrom"
                    label={t("tariff.inputs.activationdate.label")}
                    className="w-full"
                    allowOlderDates={true}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Basis for Bonus Calculation */}
                  <SelectInputField
                    form={form}
                    name="basisForBonusCalculation"
                    label={t("tariff.inputs.basisforbonuscalculation.label")}
                    className="w-full flex flex-col"
                  >
                    <SelectItem value={BasisForBonusType.Basewage}>
                      {t("tariff.inputs.basisforbonuscalculation.options.basewage")}
                    </SelectItem>
                    <SelectItem value={BasisForBonusType.Hourlywage}>
                      {t("tariff.inputs.basisforbonuscalculation.options.hourlywage")}
                    </SelectItem>
                  </SelectInputField>

                  {/* Workdays Per Year */}
                  <FormattedNumberInputField
                    control={form.control}
                    name="workdaysPerYear"
                    label={t("tariff.inputs.workdaysperyear.label")}
                    placeholder={t("tariff.inputs.workdaysperyear.placeholder")}
                    additionalSuffix={t("tariff.inputs.workdaysperyear.additionalSuffix")}
                    spaceBetweenSuffix={true}
                    style="decimal"
                    isInteger
                    className="w-full"
                    isStandalone
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Start Night */}
                  <InputField
                    form={form}
                    name="startNight"
                    label={t("tariff.inputs.startNight.label")}
                    type="time"
                    className="w-full"
                  />

                  {/* End Night */}
                  <InputField
                    form={form}
                    name="endNight"
                    label={t("tariff.inputs.endNight.label")}
                    type="time"
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {
                    formInputs.map((input) => (
                      <div className="flex flex-col gap-2 col-span-2" key={input.groupName}>
                        <h3 className="text-sm font-medium">{input.groupName}</h3>
                        <Separator />
                        <div className="flex flex-col gap-2">
                          <div className="grid gap-2 items-center grid-flow-col auto-cols-fr text-xs text-slate-500">
                            <div className="col-span-2"></div>
                            <div className="col-span-5">{t("tariff.relative")}</div>
                            <div className="col-span-5">{t("tariff.absolute")}</div>
                          </div>
                          {
                            input.fields.map((field) => (
                              <div className="grid gap-2 grid-flow-col auto-cols-fr items-start" key={field.name}>
                                <div className="text-slate-500 text-sm col-span-2 my-auto">{field.name}</div>
                                {field.inputs.map((input) => (
                                  <div key={input.name} className="flex flex-col gap-2 col-span-5">
                                    <FormattedNumberInputField
                                      control={form.control}
                                      name={input.name}
                                      placeholder={input.placeholder}
                                      style={input.style as "decimal" | "currency"}
                                      additionalSuffix={input.additionalSuffix}
                                    />
                                    {/* Error message */}
                                    {form.formState.errors[input.name] && (
                                      <p className="input-error-message">
                                        {form.formState.errors[input.name]?.message as string}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.close")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createTariffLoading}>
                <CircleLoader visible={createTariffLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddTariffDialog;
